import { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from "recharts";
import { Trophy, Medal, ChartLine } from "@phosphor-icons/react";
import api from "../lib/api";
import { scoreBandClass, scoreBandLabel } from "../lib/utils-extra";

export default function Analytics() {
  const [ranking, setRanking] = useState([]);
  const [dist, setDist] = useState({ distribution: [], count: 0 });
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/ranking"),
      api.get("/analytics/distribution?bins=10"),
      api.get("/analytics/trend"),
    ]).then(([r, d, t]) => {
      setRanking(r.data); setDist(d.data); setTrend(t.data);
    });
  }, []);

  // Aggregate trend by date (avg)
  const trendData = (() => {
    const m = {};
    trend.forEach((e) => { m[e.date] = m[e.date] || []; m[e.date].push(e.score); });
    return Object.entries(m).map(([date, arr]) => ({
      date, avg: arr.reduce((s, x) => s + (x || 0), 0) / arr.length,
    })).sort((a, b) => a.date.localeCompare(b.date));
  })();

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <div className="small-label">Insights</div>
        <h1 className="font-display text-3xl font-semibold mt-1">Performance Analytics</h1>
        <p className="text-[#475569] text-sm mt-1">Natural score distribution — no forced ranking.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="surface-card border-[#E2E8F0]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="small-label">Score Distribution</div>
                <div className="font-display text-lg font-semibold mt-1">Bell Curve</div>
                <div className="text-xs text-[#475569]">{dist.count} finalized evaluations</div>
              </div>
              <ChartLine size={22} weight="duotone" color="#0EA5E9" />
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minHeight={250}>
                <BarChart data={dist.distribution} margin={{ top: 10, right: 10, bottom: 5, left: -20 }}>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                  <XAxis dataKey="range" tick={{ fontSize: 11, fill: "#475569" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#475569" }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", background: "#fff" }} />
                  <Bar dataKey="count" fill="#0EA5E9" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="surface-card border-[#E2E8F0]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="small-label">Trend</div>
                <div className="font-display text-lg font-semibold mt-1">Average score over time</div>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minHeight={250}>
                <LineChart data={trendData} margin={{ top: 10, right: 10, bottom: 5, left: -20 }}>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#475569" }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#475569" }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", background: "#fff" }} />
                  <Line type="monotone" dataKey="avg" stroke="#0EA5E9" strokeWidth={2.5} dot={{ r: 3, fill: "#0EA5E9" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="small-label">Employee Ranking</div>
              <div className="font-display text-lg font-semibold mt-1">Top to bottom</div>
            </div>
            <Trophy size={22} weight="duotone" color="#D97706" />
          </div>
          {ranking.length === 0 && <div className="text-sm text-[#475569]">No data yet.</div>}
          <div className="space-y-2">
            {ranking.map((r) => (
              <div key={r.employee_id} className="flex items-center gap-4 p-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]"
                   data-testid={`rank-row-${r.rank}`}>
                <div className="w-9 h-9 rounded-xl bg-white border border-[#E2E8F0] flex items-center justify-center font-display font-semibold text-sm">
                  #{r.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.name}</div>
                  <div className="text-xs text-[#475569] truncate">{r.position} · {r.department}</div>
                </div>
                <Badge className={`${scoreBandClass(r.score)} text-white border-0`}>{r.interpretation}</Badge>
                <div className="font-mono-data font-semibold text-sm w-16 text-right">{r.score?.toFixed(1)}%</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
